import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

import useLayoutStore from "../../store/useLayoutStore";
import useAppStore from "../../store/useAppStore";
import useAuthStore from "../../store/useAuthStore";

const CalendarItemDetailCard = () => {
    const { t } = useTranslation("calendar");
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme);
    const selectedCalendarRequestId = useAppStore(state => state.selectedCalendarRequestId);
    const setSelectedCalendarRequestId = useAppStore(state => state.setSelectedCalendarRequestId);
    // čuva ID zahteva koji se briše ili null ako modal nije otvoren
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(null);

    const userRole = useAuthStore(state => state.userRole);
    const userId = useAuthStore(state => state.userId);
    const isHR = useAuthStore(state => state.isHR);
    const hasHR = useAppStore(state => state.hasHR);
    const isAdmin = userRole === 'admin';

    const requestData = useMemo(() => {
        if (!selectedCalendarRequestId) return null;
        const state = useAppStore.getState();
        const calendarData = state.calendarData;
        const employeesData = state.employeesData;
        const item = calendarData?.find((x) => x._id === selectedCalendarRequestId) ?? null;
        if (!item) return null;
        const pad = (n) => String(n).padStart(2, '0');
        const startDate = `${pad(item.dan_pocetak)}-${pad(item.mesec_pocetak)}-${item.godina_pocetak}`;
        const hasEnd = item.dan_kraj != null && item.mesec_kraj != null && item.godina_kraj != null;
        const endDate = hasEnd
            ? `${pad(item.dan_kraj)}-${pad(item.mesec_kraj)}-${item.godina_kraj}`
            : null;
        const baseImgUrl = import.meta.env.VITE_API_IMAGES_URL;
        const submitterEmp = employeesData?.[item.user_id];
        const submitter = submitterEmp ? `${submitterEmp.ime} ${submitterEmp.prezime}` : null;
        const submitterImageUrl = submitterEmp?.image ? `${baseImgUrl}/employees/${submitterEmp.image}` : null;
        const approvedByEmp = item.potvrda && item.potvrdio_user_id
            ? employeesData?.[item.potvrdio_user_id]
            : null;
        const approvedBy = !item.potvrda
            ? null
            : (approvedByEmp ? `${approvedByEmp.ime} ${approvedByEmp.prezime}` : null);
        const approvedByNotApproved = !item.potvrda;
        const approvedByIndefinite = item.potvrda && !approvedByEmp;
        return {
            startDate,
            endDate,
            type: item.type,
            confirmed: item.potvrda,
            rejected: item.odbijen,
            submitter,
            submitterImageUrl,
            approvedBy,
            approvedByNotApproved,
            approvedByIndefinite,
            user_id: item.user_id,
        };
    }, [selectedCalendarRequestId]);

    // Ako je hasHR false: admin vidi dugmad; ako je hasHR true: samo HR vidi ta dugmad (ista logika)
    const canEdit = requestData && ((hasHR && isHR) || (!hasHR && isAdmin) || (!requestData.confirmed && String(requestData.user_id) === String(userId)));
    const canConfirm = ((!hasHR && isAdmin) || (hasHR && isHR)) && !requestData?.confirmed && !requestData?.rejected;

    const deleteAbsenceRequest = useAppStore(state => state.deleteAbsenceRequest);
    const confirmAbsenceRequest = useAppStore(state => state.confirmAbsenceRequest);
    const rejectAbsenceRequest = useAppStore(state => state.rejectAbsenceRequest);

    const content = (
        <>
            <AnimatePresence mode="wait">
                {requestData && (
                    <motion.div
                        key={selectedCalendarRequestId}
                        className="dv-edit-modal-container"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{
                            duration: 0.14,
                            ease: "easeInOut",
                        }}
                        style={{ pointerEvents: "auto" }}
                        onClick={() => setSelectedCalendarRequestId(null)}
                        role="presentation"
                    >
                        <motion.div
                            className={`absence-modal ${isDarkTheme ? "dark-absence-modal" : "white-absence-modal"}`}
                            initial={{ opacity: 0, scale: 0.4 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.4 }}
                            transition={{ duration: 0.14, ease: "easeInOut" }}
                            style={{ pointerEvents: "auto" }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="absence-modal-close-buttons-container">
                                {canEdit && (
                                    <button
                                        type="button"
                                        className={`new-absence-modal-close-button ${isDarkTheme ? "filter-dark-buttons" : ""}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            // Sačuvaj trenutni zahtev_id u state za modal, pa tek onda očisti selekciju
                                            setIsDeleteConfirmOpen(selectedCalendarRequestId);
                                            setSelectedCalendarRequestId(null);
                                        }}
                                        aria-label={t("actions.deleteAria")}
                                    >
                                        <img src="/cards/kanta.png" alt={t("actions.deleteAria")} width="28" height="28" />
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className={`new-absence-modal-close-button ${isDarkTheme ? "filter-dark-buttons" : ""}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedCalendarRequestId(null);
                                    }}
                                    aria-label={t("actions.closeAria")}
                                >
                                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "block", margin: "auto" }}>
                                        <line x1="7.2" y1="7.2" x2="20.8" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                                        <line x1="20.8" y1="7.2" x2="7.2" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                                    </svg>
                                </button>
                            </div>
                            <h3>{requestData.submitter ?? t("status.emDash")}</h3>
                            <div className="absence-modal-content-container">
                                <div className="absence-modal-content-labels">
                                    <span>{t("detail.startDate")}</span>
                                    <span>{t("detail.endDate")}</span>
                                    <span>{t("detail.absenceType")}</span>
                                    <span>{t("detail.submitter")}</span>
                                    <span>{t("detail.approvedBy")}</span>
                                </div>
                                <div className="absence-modal-content-values">
                                    <span>{requestData.startDate}</span>
                                    <span>{requestData.endDate ?? t("status.indefinite")}</span>
                                    <span>{t(`absenceTypes.${requestData.type}`)}</span>
                                    <span>{requestData.submitter ?? t("status.emDash")}</span>
                                    <span>
                                        {requestData.approvedByNotApproved
                                            ? t("status.notApproved")
                                            : (requestData.approvedByIndefinite
                                                ? t("status.indefinite")
                                                : requestData.approvedBy)}
                                    </span>
                                </div>
                                <div className="absence-modal-content-user-img">
                                    <img
                                        className="absence-modal-user-img"
                                        src={requestData.submitterImageUrl || "/views/login.svg"}
                                        alt={requestData.submitter ?? t("status.emDash")}
                                        crossOrigin="use-credentials"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.classList.add("dv-card-img-error");
                                            e.target.src = "/views/login.svg";
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="absence-modal-actions" >
                                {canConfirm && (
                                    <button
                                        type="button"
                                        className={`absence-modal-change-button ${isDarkTheme ? "dark-big-btn" : ""}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            rejectAbsenceRequest({ zahtev_id: selectedCalendarRequestId });
                                            setSelectedCalendarRequestId(null);
                                        }}
                                        style={{ pointerEvents: "auto" }}
                                    >
                                        {t("actions.reject")}
                                    </button>
                                )}
                                {canConfirm && (
                                    <button
                                        type="button"
                                        className={`absence-modal-confirm-button ${isDarkTheme ? "dark-big-btn" : ""}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            confirmAbsenceRequest({ zahtev_id: selectedCalendarRequestId });
                                            setSelectedCalendarRequestId(null);
                                        }}
                                        style={{ pointerEvents: "auto" }}
                                    >
                                        {t("actions.confirm")}
                                    </button>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
            <AnimatePresence>
                {isDeleteConfirmOpen && (
                    <motion.div
                        className="dv-edit-modal-container"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.14, ease: "easeInOut" }}
                        style={{ pointerEvents: "auto" }}
                        onClick={(e) => {
                            if (e.target === e.currentTarget) {
                                setIsDeleteConfirmOpen(false);
                            }
                        }}
                        role="presentation"
                    >
                        <motion.div
                            className={`dv-delete-confirm-modal ${isDarkTheme ? "dark-dv-delete-confirm-modal" : "white-dv-delete-confirm-modal"}`}
                            initial={{ opacity: 0, scale: 0.4 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.4 }}
                            transition={{ duration: 0.14, ease: "easeInOut" }}
                            style={{ pointerEvents: "auto" }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                className={`new-absence-modal-close-button ${isDarkTheme ? "filter-dark-buttons" : ""}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsDeleteConfirmOpen(null);
                                }}
                                aria-label={t("actions.closeAria")}
                                style={{ position: "absolute", top: "19px", right: "19px" }}
                            >
                                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "block", margin: "auto" }}>
                                    <line x1="7.2" y1="7.2" x2="20.8" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                                    <line x1="20.8" y1="7.2" x2="7.2" y2="20.8" stroke="#08293e" strokeWidth="2.4" strokeLinecap="round" />
                                </svg>
                            </button>
                            <h3>{t("deleteConfirm.title")}</h3>
                            <p>
                                {t("deleteConfirm.messageBefore")}
                                <b>{t("deleteConfirm.messageBold")}</b>
                                {t("deleteConfirm.messageAfter")}
                            </p>
                            <div className="dv-delete-confirm-modal-buttons">
                                <button
                                    className={`dv-delete-confirm-modal-button cancel-btn ${isDarkTheme ? "dark-btn-close" : ""}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsDeleteConfirmOpen(null);
                                    }}
                                >
                                    {t("actions.cancel")}
                                </button>
                                <button
                                    className={`dv-delete-confirm-modal-button confirm-btn ${isDarkTheme ? "dark-big-btn" : ""}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        // koristi ID sačuvan u isDeleteConfirmOpen
                                        deleteAbsenceRequest({ zahtev_id: isDeleteConfirmOpen });
                                        setIsDeleteConfirmOpen(null);
                                    }}
                                >
                                    {t("actions.confirm")}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );

    return createPortal(content, document.body);
};

export default CalendarItemDetailCard;
